/**
 * Hook for managing redaction reasons.
 * Provides CRUD operations and caching for reasons.
 */

import { useState, useEffect, useCallback } from 'react';
import { reasonsApi, apiHelpers } from '../lib/api';
import type { CreateReasonRequest, ReasonLegalFields } from '../lib/api';
import type { RedactionReason } from '../types';

// Every mounted useReasons() list (the case editor's, the reasons manager's)
// hears about a reason created anywhere, so a reason made from a picker shows
// up in all pickers at once instead of only in a local copy (FB-1013).
type CreatedListener = (reason: RedactionReason) => void;
const createdListeners = new Set<CreatedListener>();

/**
 * Create a reason and add it to every mounted reasons list.
 * Throws the API error so the caller can render it (apiHelpers.handleError).
 */
export async function createSharedReason(data: CreateReasonRequest): Promise<RedactionReason> {
  const reason = await reasonsApi.createReason(data);
  createdListeners.forEach((listener) => listener(reason));
  return reason;
}

/** Hear about every reason createSharedReason makes; returns the unsubscribe. */
export function subscribeToCreatedReasons(listener: CreatedListener): () => void {
  createdListeners.add(listener);
  return () => {
    createdListeners.delete(listener);
  };
}

interface UseReasonsReturn {
  reasons: RedactionReason[];
  isLoading: boolean;
  error: string | null;
  createReason: (title: string, description?: string, legal?: ReasonLegalFields) => Promise<RedactionReason | null>;
  updateReason: (
    id: string,
    title: string,
    description?: string,
    legal?: ReasonLegalFields,
  ) => Promise<RedactionReason | null>;
  deleteReason: (id: string) => Promise<boolean>;
  refresh: () => Promise<void>;
}

export function useReasons(): UseReasonsReturn {
  const [reasons, setReasons] = useState<RedactionReason[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch all reasons
  const fetchReasons = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await reasonsApi.getReasons({ limit: 1000 });
      setReasons(response.reasons);
    } catch (err) {
      const errorMessage = apiHelpers.handleError(err);
      setError(errorMessage);
      console.error('Failed to fetch reasons:', errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchReasons();
  }, [fetchReasons]);

  // Reasons created elsewhere (another list, or a picker's "Create reason")
  useEffect(
    () =>
      subscribeToCreatedReasons((reason) =>
        setReasons(prev => (prev.some(r => r.id === reason.id) ? prev : [reason, ...prev])),
      ),
    [],
  );

  // Create a new reason
  const createReason = useCallback(async (
    title: string,
    description?: string,
    legal?: ReasonLegalFields,
  ): Promise<RedactionReason | null> => {
    try {
      // Added to this list (and every other one) by the listener above.
      return await createSharedReason({ title, description, ...legal });
    } catch (err) {
      const errorMessage = apiHelpers.handleError(err);
      setError(errorMessage);
      console.error('Failed to create reason:', errorMessage);
      return null;
    }
  }, []);

  // Update an existing reason
  const updateReason = useCallback(async (
    id: string,
    title: string,
    description?: string,
    legal?: ReasonLegalFields,
  ): Promise<RedactionReason | null> => {
    try {
      const updatedReason = await reasonsApi.updateReason(id, { title, description, ...legal });
      setReasons(prev =>
        prev.map(r => r.id === id ? updatedReason : r)
      );
      return updatedReason;
    } catch (err) {
      const errorMessage = apiHelpers.handleError(err);
      setError(errorMessage);
      console.error('Failed to update reason:', errorMessage);
      return null;
    }
  }, []);

  // Delete a reason
  const deleteReason = useCallback(async (id: string): Promise<boolean> => {
    try {
      await reasonsApi.deleteReason(id);
      setReasons(prev => prev.filter(r => r.id !== id));
      return true;
    } catch (err) {
      const errorMessage = apiHelpers.handleError(err);
      setError(errorMessage);
      console.error('Failed to delete reason:', errorMessage);
      return false;
    }
  }, []);

  // Refresh reasons
  const refresh = useCallback(async () => {
    await fetchReasons();
  }, [fetchReasons]);

  return {
    reasons,
    isLoading,
    error,
    createReason,
    updateReason,
    deleteReason,
    refresh,
  };
}
